// src/components/edge-nodes/EdgeNodeForm.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { edgeNodesService } from '../../services/edge-nodes';
import Loading from '../common/Loading';

const EdgeNodeForm = ({ mode = 'edit' }) => {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const isEditMode = mode === 'edit';
  
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    edge_node_id: '',
    hostname: '',
    firmware_version: '',
    ot_sentry_agent_version: '',
    custom_tags: {}
  });

  // For custom tags input
  const [customTagKey, setCustomTagKey] = useState('');
  const [customTagValue, setCustomTagValue] = useState('');

  useEffect(() => {
    if (isEditMode && nodeId) {
      fetchEdgeNode();
    }
  }, [isEditMode, nodeId]);

  const fetchEdgeNode = async () => {
    try {
      setLoading(true);
      const data = await edgeNodesService.getEdgeNode(nodeId.trim());
      
      setFormData({
        edge_node_id: data.edge_node_id || '',
        hostname: data.hostname || '',
        firmware_version: data.firmware_version || '',
        ot_sentry_agent_version: data.ot_sentry_agent_version || '',
        custom_tags: data.custom_tags || {}
      });
      
      setError(null);
    } catch (err) {
      console.error('Error fetching edge node:', err);
      setError('Failed to load edge node data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCustomTag = () => {
    if (customTagKey.trim() && customTagValue.trim()) {
      setFormData(prev => ({
        ...prev,
        custom_tags: {
          ...prev.custom_tags,
          [customTagKey.trim()]: customTagValue.trim()
        }
      }));
      setCustomTagKey('');
      setCustomTagValue('');
    }
  };

  const handleRemoveCustomTag = (key) => {
    setFormData(prev => {
      const newTags = { ...prev.custom_tags };
      delete newTags[key];
      return { ...prev, custom_tags: newTags };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (isEditMode) {
        // Update existing edge node
        const updateData = {
          edge_node_id: formData.edge_node_id,
          hostname: formData.hostname,
          custom_tags: Object.keys(formData.custom_tags).length > 0 ? formData.custom_tags : {}
        };
        
        await edgeNodesService.updateEdgeNode(nodeId.trim(), updateData);
        
        setSuccess(true);
        
        // Redirect back to detail page after a short delay
        setTimeout(() => {
          navigate(`/edge-nodes/${nodeId}`);
        }, 1500);
      } else {
        // Create new edge node
        // Note: For creation, you should probably use the registration endpoint instead
        setError('Create mode not yet implemented. Please use the Register New Edge Node button.');
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error saving edge node:', err);
      setError(err.response?.data?.detail || 'Failed to save edge node. Please check your input and try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading edge node data..." />;
  }

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {isEditMode ? 'Edit Edge Node' : 'Create Edge Node'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {isEditMode 
              ? 'Update the configuration for this edge node.'
              : 'Create a new edge node configuration.'
            }
          </p>

          {success && (
            <div className="mt-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Edge node successfully updated! Redirecting...
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-6">
              
              {/* Edge Node ID - Read-only in edit mode */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="edge_node_id" className="block text-sm font-medium text-gray-700">
                  Edge Node ID
                </label>
                <input
                  type="text"
                  name="edge_node_id"
                  id="edge_node_id"
                  disabled={isEditMode}
                  value={formData.edge_node_id}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                />
                {isEditMode && (
                  <p className="mt-1 text-xs text-gray-500">Edge Node ID cannot be changed</p>
                )}
              </div>

              {/* Hostname - Editable */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="hostname" className="block text-sm font-medium text-gray-700">
                  Hostname
                </label>
                <input
                  type="text"
                  name="hostname"
                  id="hostname"
                  value={formData.hostname}
                  onChange={handleChange}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              {/* Firmware Version - Read-only (updated by the device) */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="firmware_version" className="block text-sm font-medium text-gray-700">
                  Firmware Version
                </label>
                <input
                  type="text"
                  name="firmware_version"
                  id="firmware_version"
                  disabled
                  value={formData.firmware_version}
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-100 text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">Set by the device itself</p>
              </div>

              {/* AI Sentry Agent Version - Read-only (updated by the device) */}
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="ot_sentry_agent_version" className="block text-sm font-medium text-gray-700">
                  AI Sentry Agent Version
                </label>
                <input
                  type="text"
                  name="ot_sentry_agent_version"
                  id="ot_sentry_agent_version"
                  disabled
                  value={formData.ot_sentry_agent_version}
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-100 text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-500">Set by the device itself</p>
              </div>

              {/* Custom Tags Section */}
              <div className="col-span-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Tags
                </label>
                
                {/* Existing Tags */}
                {Object.keys(formData.custom_tags).length > 0 && (
                  <div className="mb-3 space-y-2">
                    {Object.entries(formData.custom_tags).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-700">{key}:</span>
                          <span className="ml-2 text-sm text-gray-600">{value}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomTag(key)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Tag */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Tag key"
                    value={customTagKey}
                    onChange={(e) => setCustomTagKey(e.target.value)}
                    className="flex-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  <input
                    type="text"
                    placeholder="Tag value"
                    value={customTagValue}
                    onChange={(e) => setCustomTagValue(e.target.value)}
                    className="flex-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomTag}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Add custom key-value pairs to tag this edge node
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <Link
                to={isEditMode ? `/edge-nodes/${nodeId}` : '/edge-nodes'}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting || success}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(submitting || success) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update Edge Node' : 'Create Edge Node'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EdgeNodeForm;
